import type { SkillDef } from "../types";
import { borrowsOf, digits, fmt, placeParts, prob } from "../util";

/** Digits of the larger and smaller number. `borrow`: false = never borrows, true = at least once, absent = either. */
const SPEC: Record<number, { d: [number, number]; borrow?: boolean }> = {
  1: { d: [2, 1], borrow: false }, 2: { d: [2, 2], borrow: false }, 3: { d: [2, 2], borrow: true },
  4: { d: [3, 2] }, 5: { d: [3, 3] }, 6: { d: [4, 3] }, 7: { d: [4, 4] }, 8: { d: [5, 4] },
  9: { d: [5, 5] }, 10: { d: [6, 5] },
};

export const sub: SkillDef = {
  key: "sub", name: "Subtraction", blurb: "compensation, left to right", start: 1,
  generate(L, r) {
    const { d: [da, db], borrow } = SPEC[L];
    let a: number, b: number;
    do {
      a = r.int(10 ** (da - 1), 10 ** da - 1); b = r.int(10 ** (db - 1), 10 ** db - 1);
      if (b > a) [a, b] = [b, a];
    } while (a === b || (borrow !== undefined && (borrowsOf(a, b) > 0) !== borrow));
    const unit = 10 ** (String(b).length - 1), up = Math.ceil(b / unit) * unit;
    let hint: string;
    if (up !== b && a >= up) {
      hint = `Round up: ${fmt(a)} − ${fmt(up)} = ${fmt(a - up)}, then add back ${fmt(up - b)} → ${fmt(a - b)}`;
    } else {
      let t = a;
      const ch = [fmt(a)];
      for (const p of placeParts(b)) { t -= p; ch.push(`−${fmt(p)} = ${fmt(t)}`); }
      hint = "Left to right: " + ch.join(", ");
    }
    return prob(`${fmt(a)} − ${fmt(b)}`, a - b, hint, { t: 1 + 0.6 * (digits(a) + digits(b)) + 0.9 * borrowsOf(a, b) });
  },
};
